import { flatten, uniq } from 'lodash';
import dayjs from 'dayjs';

// Treats two delivery/early-delivery time values as equal both when they represent the same
// calendar day (compared with dayjs at 'day' granularity, consistent with how delivery dates are
// compared elsewhere in the app - see getInputErrors in orderitem.jsx - since the values may be
// Date objects, ISO strings with differing time-of-day components, or otherwise not === equal
// even though they represent the same delivery day) and when both are missing (e.g. legacy/test
// data or draft orders where the schedule hasn't been set yet).
const isSameDeliveryTimeValue = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return dayjs(a).isSame(dayjs(b), 'day');
};

/**
 * Computes, for a given order, which pallet codes it could potentially be grouped into (based on
 * orders that share the same delivery address/time/recipient), plus the set of all pallet codes
 * currently in use anywhere - both used as input to `determineAllowedPalletCodesInSelection`,
 * which applies the actual per-order harmonization rules on top of this data.
 *
 * This is the FIRST step when opening `PalletInfoSelectorDialog`: call this function first to
 * gather `allowedPalletCodes`/`allUsedCodes`, then pass its result into
 * `determineAllowedPalletCodesInSelection` together with the order (and, optionally, the specific
 * filters) being edited.
 *
 * @param {object} orderInScope - The order being edited (the one whose pallet code assignment may
 *   change). Only orders considered its "siblings" (see filtering rule below) contribute pallet
 *   codes to the result.
 * @param {object[]} allOrders - Candidate orders to consider as potential siblings of
 *   `orderInScope` for pallet grouping purposes. This should be the broadest reasonable pool
 *   available (e.g. all pending orders in the basket) - NOT just orders the user happens to have
 *   checked in a list - since this function itself narrows the pool down to real siblings via the
 *   delivery address/time/recipient filter below. Passing too narrow a pool (e.g. only
 *   checkbox-selected orders) can wrongly exclude valid siblings and starve the dialog of options.
 *   `orderInScope` itself does not need to be (but may be) included in this array; if included, it
 *   contributes its own already-used code(s) like any other candidate.
 * @returns {[string[], {code: string, locations: string[]}[]]} A 2-element tuple:
 *   - `[0]` `allUsedPalletCodes` - every real (non-null/non-empty) pallet code in use across
 *     `allOrders`.
 *   - `[1]` `potentialCodes` - the codes allowed for `orderInScope` given its siblings, each as
 *     `{ code, locations }` where `locations` lists the location/recipient name(s) of the sibling
 *     order(s) currently using that code. This is passed to `determineAllowedPalletCodesInSelection`
 *     as `allowedCodes`.
 *
 * Business rule (mirrors the backend's automatic allocation in
 * `PalletInformationAllocator.SetPalletInformation`): orders may only share a pallet group if they
 * share the same delivery address AND the same delivery time / early delivery time / recipient.
 * Orders with a matching delivery address but a different delivery schedule are NOT considered
 * siblings and must not be offered as allowed pallet groups. Delivery time equality is compared at
 * day granularity (see `isSameDeliveryTimeValue`) since real data may hold Date objects or ISO
 * strings with differing time-of-day components that still represent the same delivery day.
 *
 * Filters (`orderedFilters`) with a `palletInformation` of `null` or `''` represent "not yet
 * allocated to any pallet group" and are filtered out everywhere in this function - they must
 * never be treated as a real, selectable pallet code.
 */
export const returnAllAndAllowedPalletCodesInOrders = (orderInScope, allOrders) => {
    const filteredLocations = allOrders.filter(
        (o) =>
            // o.deliveryAddress.name === orderInScope.deliveryAddress.name &&
            o.deliveryAddress.streetAddress === orderInScope.deliveryAddress.streetAddress &&
            o.deliveryAddress.zipCode === orderInScope.deliveryAddress.zipCode &&
            o.deliveryAddress.city === orderInScope.deliveryAddress.city &&
            isSameDeliveryTimeValue(o.deliveryTime, orderInScope.deliveryTime) &&
            isSameDeliveryTimeValue(o.earlyDeliveryTime, orderInScope.earlyDeliveryTime)
    );

    const allowedPalletCodesAndLocations = flatten(
        filteredLocations.map((o) => {
            return uniq(o.orderedFilters.map((fo) => fo.palletInformation))
                .filter((c) => c != null && c !== '')
                .map((c) => {
                    return { name: o.location ? o.location.name : o.deliveryRecipient, code: c };
                });
        })
    );
    const allowedPalletCodes = uniq(
        flatten(
            filteredLocations.map((o) => {
                return o.orderedFilters;
            })
        )
            .map((fo) => fo.palletInformation)
            .filter((c) => c != null && c !== '')
    );

    const potentialCodes = allowedPalletCodes.map((c) => {
        return {
            code: c,
            locations: allowedPalletCodesAndLocations.filter((cl) => cl.code === c).map((loc) => loc.name),
        };
    });

    const allUsedPalletCodes = uniq(
        flatten(
            allOrders.map((o) => {
                return o.orderedFilters;
            })
        )
            .map((fo) => fo.palletInformation)
            .filter((c) => c != null && c !== '')
    );

    return [allUsedPalletCodes, potentialCodes];
};

export const getPalletInfosUsedInOrder = (order) => {
    if (order && order.orderedFilters) {
        return uniq(order.orderedFilters.map((fo) => fo.palletInformation));
    }
    return [];
};

const getFreshPalletCode = (allUsedCodes, availablePalletCodes) => {
    for (const c of availablePalletCodes) {
        if (!allUsedCodes.includes(c)) {
            return { code: c, locations: [] };
        }
    }
    throw new Error('Could not allocate pallet code');
};

/**
 * Given the candidate codes/usage data already computed by `returnAllAndAllowedPalletCodesInOrders`,
 * determines the final list of pallet codes that should actually be offered as selectable options
 * in `PalletInfoSelectorDialog` for a specific order (or a specific subset of its filters).
 *
 * This is the SECOND step when opening the dialog: `returnAllAndAllowedPalletCodesInOrders` finds
 * the raw candidate codes from sibling orders; this function then applies the harmonization rules
 * (below) to decide which of those candidates make sense to offer, and whether a brand-new/"fresh"
 * pallet code needs to be added to the list.
 *
 * @param {object} orderInScope - The order being edited. When `fosInScope` is not provided, this
 *   function reasons about the WHOLE order (i.e. every filter in `orderInScope.orderedFilters`).
 * @param {object[]|undefined|null} fosInScope - Optional: the specific subset of
 *   `orderInScope.orderedFilters` being edited, when the user opened the dialog for only some of an
 *   order's filters rather than the whole order (e.g. per-row selection). If omitted/empty, the
 *   whole order is considered in scope.
 * @param {{code: string, locations: string[]}[]} allowedCodes - The candidate codes for
 *   `orderInScope`'s siblings, as returned in `[1]` of `returnAllAndAllowedPalletCodesInOrders`.
 * @param {string[]} allUsedCodes - Every pallet code in use anywhere, as returned in `[0]` of
 *   `returnAllAndAllowedPalletCodesInOrders` - used to compute a fresh, non-colliding code when
 *   needed.
 * @param {string[]} availablePalletCodes - The full ordered list of possible pallet code letters
 *   (e.g. `['A', 'B', ...]`) from which a fresh code is picked (the first one not already in
 *   `allUsedCodes`).
 * @returns {{code: string, locations: string[]}[]} The final list of selectable pallet codes to
 *   render in the dialog's dropdown. Never returns an empty array when a fresh code can be
 *   allocated - see the "fresh code fallback" rule below - so the dialog should not need to handle
 *   "no options at all" as long as `availablePalletCodes` isn't exhausted (in which case
 *   `getFreshPalletCode` throws).
 *
 * Harmonization rules:
 * 1) By default, any of the given `allowedCodes` may be selected.
 * 2) A code that `orderInScope` (or the in-scope filters) is ALREADY fully/exclusively using is
 *    excluded from the options, since re-selecting it would be a no-op - UNLESS only PART of the
 *    order is currently using that code (some filters still unallocated), in which case the code
 *    remains selectable so the remaining unallocated filters can be merged into the same group.
 * 3) If the order's own code is shared with another location/order (i.e. removing it from the
 *    options per rule 2 would otherwise leave the order with no way to "split off" into its own new
 *    group), a fresh/new pallet code is added to the options.
 * 4) If the order (or the in-scope filter selection) currently uses MULTIPLE distinct real pallet
 *    codes, and every one of them is exclusively used by this order alone, a fresh code is offered
 *    alongside all of them so the filters can be harmonized into one brand-new group.
 * 5) Fresh code fallback: if the above rules would otherwise leave zero options (e.g. a lone
 *    order/selection with no sibling orders sharing its delivery address/time, whether unallocated
 *    or already the sole user of its own code), a fresh pallet code is offered instead of an empty
 *    list - the dialog must never claim "filters can't be moved to a different pallet" when
 *    allocating a brand new pallet group is in fact always possible.
 *
 * Filters with a `palletInformation` of `null` or `''` mean "not yet allocated to any pallet
 * group" - they are excluded (via `realUsedCodesOrder`) from being treated as a real "code in use"
 * that needs harmonizing or looking up in `allowedCodes`.
 */
export const determineAllowedPalletCodesInSelection = (
    orderInScope,
    fosInScope,
    allowedCodes,
    allUsedCodes,
    availablePalletCodes
) => {
    const usedCodesOrder = getPalletInfosUsedInOrder(orderInScope);
    // Filters that have not been allocated to any pallet group yet report a null/empty
    // palletInformation - that is not an actual "code in use" that needs harmonizing or
    // looking up from allowedCodes, so it must be excluded before reasoning about what's
    // currently in use. Otherwise an order (or filter selection) that isn't allocated to
    // any pallet group yet would crash instead of simply allowing any of the allowed codes
    // to be picked.
    const realUsedCodesOrder = uniq(usedCodesOrder.filter((c) => c != null && c !== ''));

    // See rule 5 (fresh code fallback) above.
    const withFreshFallback = (codes) => {
        if (codes.length === 0) {
            return [getFreshPalletCode(allUsedCodes, availablePalletCodes)];
        }
        return codes;
    };

    if (fosInScope && fosInScope.length > 0) {
        const allSelected = fosInScope.length === orderInScope.orderedFilters.length;
        const allowed = allowedCodes;
        if (allSelected) {
            if (realUsedCodesOrder.length === 0) {
                return withFreshFallback(allowed);
            }
            let addFresh = true;
            for (const c of realUsedCodesOrder) {
                if (allowedCodes.find((cod) => cod.code === c).locations.length === 1) {
                    addFresh = false;
                    break;
                }
            }
            if (addFresh) {
                return [...allowed, getFreshPalletCode(allUsedCodes, availablePalletCodes)];
            }
            return withFreshFallback(allowed);
        } else {
            return [...allowed, getFreshPalletCode(allUsedCodes, availablePalletCodes)];
        }
    } else {
        if (realUsedCodesOrder.length === 0) {
            // Order (or all of its filters) has not been allocated to any pallet group yet -
            // any of the allowed codes can be freely picked (or a fresh one, if none available).
            return withFreshFallback(allowedCodes);
        } else if (realUsedCodesOrder.length > 1) {
            let addFresh = true;
            for (const c of realUsedCodesOrder) {
                if (allowedCodes.find((cod) => cod.code === c).locations.length === 1) {
                    addFresh = false;
                    break;
                }
            }
            if (addFresh) {
                return [...allowedCodes, getFreshPalletCode(allUsedCodes, availablePalletCodes)];
            }
            return withFreshFallback(allowedCodes);
        } else {
            // The order's own code is only excluded from the options when the WHOLE order is
            // already homogeneously using it (selecting it again would be a no-op). If some of
            // the order's filters are still unallocated (or, in principle, mixed with other real
            // codes - though that's handled above), the code must remain selectable so the
            // remaining filters can be merged into it.
            const orderFullyUsesSingleCode = orderInScope.orderedFilters.every(
                (fo) => fo.palletInformation === realUsedCodesOrder[0]
            );
            if (!orderFullyUsesSingleCode) {
                return withFreshFallback(allowedCodes);
            }

            const allowed = allowedCodes.filter((c) => c.code !== realUsedCodesOrder[0]);
            const codeInUse = allowedCodes.find((c) => c.code === realUsedCodesOrder[0]);

            if (codeInUse.locations.length > 1) {
                return [...allowed, getFreshPalletCode(allUsedCodes, availablePalletCodes)];
            }
            return withFreshFallback(allowed);
        }
    }
};
